import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Drawer,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ClickAwayListener,
} from "@mui/material";
import { useState, useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";

// Hardcoded GIFs for current version
const HARDCODED_GIFS = [
  {
    id: "1",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExcDd2OWF6Y2RrMnNyNnRiMHRqMjN0ZHJiOXdyOXVhNjRhbHB6YjBjYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l0MYt5jPR6QX5pnqM/giphy.gif",
    name: "Party Dance",
    tags: ["dance", "party", "celebrate"],
  },
  {
    id: "2",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExM2t4cWRtZWNwYzF0Y2NnZmduMnN3cXpsYm80eDg3YjB0amF1dXZhZyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7abKhOpu0NwenH3O/giphy.gif",
    name: "Spongebob Thumbs Up",
    tags: ["reaction", "approve", "thumbs up"],
  },
  {
    id: "3",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExaHd0NnVsMHN4aW1mNHFtMjhkMTFqNGc4NnlvNXhkOGpjMW5iZDV5eCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/26ufdipQqU2lhNA4g/giphy.gif",
    name: "Sparkle",
    tags: ["sparkle", "shiny", "magic"],
  },
  {
    id: "4",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExOGhxdWJhb2lmMGN2NXFidzRhcW0zc3RkaHQ3ZTFzbXN0am5qZHZ1bSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/artj92V8o75VPL7AeQ/giphy.gif",
    name: "Confetti Celebration",
    tags: ["celebrate", "confetti", "party"],
  },
  {
    id: "5",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExNm9wMGVzZjhiMjJtcWdhdnB6dHBhZnNsMnRuZDN0dG4wNHB5NjN2ZSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l3q2K5jinAlChoCLS/giphy.gif",
    name: "Mind Blown",
    tags: ["wow", "shocked", "reaction"],
  },
  {
    id: "6",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExNHh0OWZ1ZXRrOGNhcjdkNnRhcHI2YnVndnRhMGRzZnQ5MHRzYnpkZSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l0HlBO7eyXzSZkJri/giphy.gif",
    name: "Waiting",
    tags: ["wait", "time", "delay"],
  },
  {
    id: "7",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExbTU2ZW00c2g2OGE5cHEzaXp2ZHE5c3A2OHZhYjJ3N2p0NXo3d2g3ZCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/xT9IgG50Fb7Mi0prBC/giphy.gif",
    name: "Wave",
    tags: ["wave", "high five", "reaction"],
  },
  {
    id: "8",
    url: "https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExdGRwMGJiNjB5NmRqNnphN25iMzltMnhiOXd2OGwxMTlrdXBscm9neSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7abB06u9bNzA8lu8/giphy.gif",
    name: "Space Adventure",
    tags: ["space", "star wars", "adventure"],
  },
];

// Collect all unique tags
const ALL_TAGS = Array.from(new Set(HARDCODED_GIFS.flatMap((gif) => gif.tags))).sort();

export const Gifs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newGifUrl, setNewGifUrl] = useState("");
  const [newGifName, setNewGifName] = useState("");
  const [newGifTags, setNewGifTags] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  // Tags not yet selected, filtered by tag search query
  const tagSuggestions = useMemo(() => {
    const available = ALL_TAGS.filter((tag) => !selectedTags.includes(tag));
    if (!tagSearch.trim()) return available.slice(0, 10);
    const filtered = available.filter((tag) =>
      tag.toLowerCase().includes(tagSearch.toLowerCase())
    );
    return filtered.slice(0, 10);
  }, [tagSearch, selectedTags]);

  const hasNoTagResults = tagSearch.trim().length > 0 && tagSuggestions.length === 0;

  const filteredGifs = HARDCODED_GIFS.filter((gif) => {
    const matchesSearch =
      !searchQuery ||
      gif.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gif.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => gif.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  const handleAddGif = () => {
    if (!newGifUrl.trim() || !newGifName.trim() || !newGifTags.trim()) {
      setSnackbar({
        open: true,
        message: "All fields (GIF URL, Name, Tags) are required",
        severity: "warning",
      });
      return;
    }

    setSnackbar({
      open: true,
      message: "Backend for this feature is under development",
      severity: "info",
    });
    setDrawerOpen(false);
    setNewGifUrl("");
    setNewGifName("");
    setNewGifTags("");
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

  return (
    <Stack spacing={2} sx={{ padding: 2 }}>
      {/* Header row: title, search, filter, add button — all in one line */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: 2,
          padding: "10px 16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="h6" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
          GIFs
        </Typography>

        {/* Search */}
        <TextField
          placeholder="Search GIFs..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flex: 1,
            maxWidth: 360,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
              "&:hover": { backgroundColor: "#ebebeb" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#999", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Tag filter */}
        <ClickAwayListener onClickAway={() => setTagDropdownOpen(false)}>
          <Box sx={{ position: "relative" }}>
            <TextField
              placeholder="Filter by tags..."
              variant="outlined"
              size="small"
              value={tagSearch}
              onChange={(e) => {
                setTagSearch(e.target.value);
                setTagDropdownOpen(true);
              }}
              onFocus={() => setTagDropdownOpen(true)}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f5f5f5",
                  "&:hover": { backgroundColor: "#ebebeb" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon sx={{ color: "#999", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            {tagDropdownOpen && (
              <Paper
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  mt: 0.5,
                  maxHeight: 300,
                  overflow: "auto",
                  borderRadius: 2,
                }}
                elevation={4}
              >
                {hasNoTagResults ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tags match your search
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {tagSuggestions.map((tag) => (
                      <ListItemButton
                        key={tag}
                        onClick={() => {
                          handleTagToggle(tag);
                          setTagSearch("");
                          setTagDropdownOpen(false);
                        }}
                      >
                        <ListItemText primary={tag} />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Paper>
            )}
          </Box>
        </ClickAwayListener>

        {/* Selected tag count badge */}
        {selectedTags.length > 0 && (
          <Chip
            label={`${selectedTags.length} tag${selectedTags.length > 1 ? "s" : ""}`}
            size="small"
            color="primary"
            onDelete={() => setSelectedTags([])}
          />
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Add button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDrawerOpen(true)}
          sx={{
            backgroundColor: "#485CF3",
            "&:hover": { backgroundColor: "#3a4cc7" },
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 500,
            px: 2.5,
          }}
        >
          Add GIF
        </Button>
      </Box>

      {/* Active tag filters */}
      {selectedTags.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {selectedTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => handleTagToggle(tag)}
            />
          ))}
        </Box>
      )}

      {/* GIF Grid */}
      {filteredGifs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No GIFs found matching your search.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2,
          }}
        >
          {filteredGifs.map((gif) => (
            <Box
              key={gif.id}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
                transition: "box-shadow 0.2s ease",
              }}
            >
              <img
                src={gif.url}
                alt={gif.name}
                style={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <Box sx={{ padding: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {gif.name}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {gif.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Add GIF Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 400, padding: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6">Add New GIF</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={3}>
            <TextField
              label="GIF URL"
              placeholder="https://example.com/gif.gif"
              fullWidth
              required
              value={newGifUrl}
              onChange={(e) => setNewGifUrl(e.target.value)}
            />

            <TextField
              label="Name"
              placeholder="Enter GIF name"
              fullWidth
              required
              value={newGifName}
              onChange={(e) => setNewGifName(e.target.value)}
            />

            <TextField
              label="Tags"
              placeholder="e.g. reaction, funny, happy"
              fullWidth
              required
              helperText="Comma-separated tags"
              value={newGifTags}
              onChange={(e) => setNewGifTags(e.target.value)}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleAddGif}
              sx={{
                backgroundColor: "#485CF3",
                "&:hover": { backgroundColor: "#3a4cc7" },
                textTransform: "none",
                py: 1.5,
              }}
            >
              Add GIF
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};
